import {
  CODICE_ADM_TIPOLOGIA,
  CODICE_ATTIVITA_SEMESTRALE,
  LETTERA_CONTATORE,
  type CodiceContatore,
  type Periodo,
  type TipologiaDichiarante,
  type TipologiaImpianto,
} from '@praticheenergia/domain'
import { avviso, bloccante, type Diagnostica } from './diagnostica'

/**
 * Import di anagrafica e contatori dall'XML AD-1 dell'anno precedente, nel
 * tracciato ufficiale ADM (docs/riferimenti-adm/): annuale `EnergiaElettrica`
 * o semestrale `EnergiaElettricaSemestrale`. Legge, non scrive: e' la fase di
 * test che il portale mostra prima di creare qualcosa.
 *
 * Il formato .c01 non e' supportato: nessun esempio ne' specifica disponibili.
 */

export interface ContatoreImportato {
  readonly codice: CodiceContatore
  readonly matricola: string
  /** Stringa, mai number: moltiplica ogni lettura. */
  readonly costanteLettura: string
  readonly tipologiaConsumo: string | null
  readonly tipoScambio: string | null
  readonly idControparte: string | null
}

export interface EsitoAnalisi {
  readonly formato: 'annuale' | 'semestrale'
  readonly codiceDitta: string | null
  readonly annoImposta: number | null
  readonly periodo: Periodo | null
  /** Solo dall'annuale: il CodAtt 00/01/02 e' la tipologia dell'impianto. */
  readonly tipologia: TipologiaImpianto | null
  /** Solo dal semestrale: il CodAtt 1-4 e' il soggetto obbligato, non l'impianto. */
  readonly tipologiaDichiarante: TipologiaDichiarante | null
  readonly contatori: readonly ContatoreImportato[]
  readonly diagnostiche: readonly Diagnostica[]
}

// Struttura del tracciato, la stessa di QUADRO_DEL_CONTATORE (quadri.ts) letta
// al contrario: i contatori X (non sottoposti, altri) non hanno un quadro.
const CODICE_DEL_QUADRO: Readonly<Record<string, CodiceContatore>> = Object.fromEntries(
  Object.entries(LETTERA_CONTATORE)
    .filter(([, lettera]) => lettera !== 'X')
    .map(([codice, lettera]) => [lettera, codice as CodiceContatore]),
)

const inverti = <K extends string>(mappa: Partial<Record<K, string>>) =>
  Object.fromEntries(Object.entries(mappa).map(([k, v]) => [v, k])) as Record<string, K>
const TIPOLOGIA_DA_CODATT = inverti(CODICE_ADM_TIPOLOGIA)
const DICHIARANTE_DA_CODATT = inverti(CODICE_ATTIVITA_SEMESTRALE)

// Un prefisso di namespace (`ns2:`) e' comune nei file prodotti da altri software.
const P = '(?:[\\w.-]+:)?'

function elementi(xml: string, tag: string): string[] {
  const re = new RegExp(`<${P}${tag}(?:\\s[^>]*)?>([\\s\\S]*?)</${P}${tag}>`, 'g')
  return [...xml.matchAll(re)].map((m) => m[1]!)
}

function testo(xml: string, tag: string): string | null {
  return elementi(xml, tag)[0]?.trim() || null
}

/** 1.0000 → 1, 40.50 → 40.5: stessa costante, senza passare da un float. */
function normalizzaDecimale(v: string): string {
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v
}

/** La radice e il suo contenuto. Cerca dentro il testo, non all'inizio: un
 * .xml.p7m firmato ha l'XML in chiaro dentro la busta binaria. */
function radice(contenuto: string): { formato: EsitoAnalisi['formato']; corpo: string } {
  for (const [nome, formato] of [
    ['EnergiaElettricaSemestrale', 'semestrale'],
    ['EnergiaElettrica', 'annuale'],
  ] as const) {
    const m = new RegExp(`<${P}${nome}(?:\\s[^>]*)?>([\\s\\S]*)</${P}${nome}>`).exec(contenuto)
    if (m) return { formato, corpo: m[1]! }
  }
  throw new Error('Il file non e una dichiarazione AD-1 in formato ADM')
}

export function analizzaXmlAdm(contenuto: string): EsitoAnalisi {
  const { formato, corpo } = radice(contenuto)
  const diagnostiche: Diagnostica[] = []

  const dich = elementi(corpo, 'Dich')[0] ?? ''
  const codiceDitta = testo(dich, 'CodDitta')
  if (!codiceDitta) diagnostiche.push(bloccante('CODICE_DITTA_MANCANTE', 'Il file non contiene il codice ditta'))
  const codAtt = testo(dich, 'CodAtt') ?? ''

  const anno = formato === 'annuale' ? testo(corpo, 'Anno') : testo(elementi(corpo, 'Periodo')[0] ?? '', 'Anno')
  const riferimento = testo(elementi(corpo, 'Periodo')[0] ?? '', 'PeriodoRiferimento')
  const periodo: Periodo | null =
    formato === 'annuale' ? 'annuale' : riferimento === '1' ? 'semestre_1' : riferimento === '2' ? 'semestre_2' : null

  const perMatricola = new Map<string, ContatoreImportato & { tipologie: Set<string> }>()
  let senzaMatricola = 0
  for (const [quadro, codice] of Object.entries(CODICE_DEL_QUADRO)) {
    for (const blocco of elementi(corpo, quadro)) {
      for (const riga of elementi(blocco, 'Contatore')) {
        const matricola = testo(riga, 'Matr')
        if (!matricola) {
          senzaMatricola++
          continue
        }
        const tipologia = testo(riga, 'Tipologia')
        const esistente = perMatricola.get(matricola)
        if (esistente) {
          if (esistente.codice !== codice) {
            diagnostiche.push(
              avviso(
                'MATRICOLA_IN_PIU_QUADRI',
                `La matricola ${matricola} compare nei quadri ${LETTERA_CONTATORE[esistente.codice]} e ${quadro}: si tiene il primo`,
              ),
            )
          } else if (tipologia) esistente.tipologie.add(tipologia)
          continue
        }
        const costLett = testo(riga, 'CostLett')
        perMatricola.set(matricola, {
          codice,
          matricola,
          costanteLettura: costLett ? normalizzaDecimale(costLett) : '1',
          tipologiaConsumo: null,
          tipoScambio: testo(riga, 'Tipo'),
          idControparte: testo(riga, 'Id'),
          tipologie: new Set(tipologia ? [tipologia] : []),
        })
      }
    }
  }

  if (senzaMatricola > 0) {
    diagnostiche.push(
      avviso('MATRICOLA_ASSENTE', `${senzaMatricola} righe di contatore senza matricola: non si possono importare`),
    )
  }

  const contatori = [...perMatricola.values()].map(({ tipologie, ...c }) => {
    // Un contatore a scaglioni compare con piu' tipologie (M6, M7…): la
    // tipologia composta non si ricostruisce a colpo sicuro, la indica l'utente.
    if (tipologie.size > 1) {
      diagnostiche.push(
        avviso(
          'TIPOLOGIA_CONSUMO_MULTIPLA',
          `Il contatore ${c.matricola} ha piu tipologie di consumo (${[...tipologie].join(', ')}): indicala dopo l import`,
        ),
      )
      return c
    }
    return { ...c, tipologiaConsumo: [...tipologie][0] ?? null }
  })

  if (contatori.length > 0) {
    diagnostiche.push(
      avviso('CIFRE_INTERE_DA_COMPLETARE', 'Il tracciato ADM non riporta le cifre intere dei contatori: completale dopo l import'),
    )
  }

  return {
    formato,
    codiceDitta,
    annoImposta: anno ? Number(anno) : null,
    periodo,
    tipologia: formato === 'annuale' ? (TIPOLOGIA_DA_CODATT[codAtt] ?? null) : null,
    tipologiaDichiarante: formato === 'semestrale' ? (DICHIARANTE_DA_CODATT[codAtt] ?? null) : null,
    contatori,
    diagnostiche,
  }
}
